<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getFileInfo } from '@lumeratec/utils';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMessageContext } from '../provider.js';

import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const { messageType } = useMessageContext();
const isDescribing = ref(false);
const attachmentDescription = ref(attachment?.attachmentDescription || '');

const fileDetails = computed(() => {
  return getFileInfo(attachment?.dataUrl || '');
});

const displayFileName = computed(() => {
  const { base, type } = fileDetails.value;
  const truncatedName = (str, maxLength, hasExt) =>
    str.length > maxLength
      ? `${str.substring(0, maxLength).trimEnd()}${hasExt ? '..' : '...'}`
      : str;

  return type
    ? `${truncatedName(base, 12, true)}.${type}`
    : truncatedName(base, 14, false);
});

const fullFileName = computed(() => {
  const name = fileDetails.value.name || t('CONVERSATION.UNKNOWN_FILE_TYPE');
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
});

const fileTypeLabel = computed(() =>
  fileDetails.value.type
    ? fileDetails.value.type.toUpperCase()
    : t('CONVERSATION.REPLYBOX.GENERIC_DOCUMENT')
);

const accountId = computed(() => store.getters.getCurrentAccountId);
const canDescribeAttachment = computed(
  () =>
    messageType.value === 0 &&
    store.getters['accounts/isFeatureEnabledonAccount'](
      accountId.value,
      FEATURE_FLAGS.LUMERAI_TASKS
    )
);

const announce = (message, priority = 'polite') => {
  window.dispatchEvent(
    new CustomEvent('a11y-announce', { detail: { message, priority } })
  );
};

const describeAttachment = async () => {
  if (isDescribing.value) return;

  try {
    isDescribing.value = true;
    announce(t('CONVERSATION.REPLYBOX.ATTACHMENT_DESCRIPTION_LOADING'));
    const { data } = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/lumerai/tasks/describe_attachment`,
      { attachment_id: attachment.id }
    );
    attachmentDescription.value = data.description;
    if (data.cached) {
      useAlert(t('CONVERSATION.REPLYBOX.DESCRIPTION_FROM_CACHE'));
    }
    announce(
      t('CONVERSATION.REPLYBOX.ATTACHMENT_DESCRIPTION_ANNOUNCEMENT', {
        description: data.description,
      }),
      'assertive'
    );
  } catch (error) {
    const message =
      error.response?.data?.error ||
      t('CONVERSATION.REPLYBOX.ATTACHMENT_DESCRIPTION_ERROR');
    useAlert(message);
    announce(message, 'assertive');
  } finally {
    isDescribing.value = false;
  }
};

const textColorClass = computed(() => {
  const colorMap = {
    '7z': 'dark:text-[#EDEEF0] text-[#2F265F]',
    csv: 'text-n-amber-12',
    doc: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    docx: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    json: 'text-n-slate-12',
    odt: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    pdf: 'text-n-slate-12',
    ppt: 'dark:text-[#FFE0C2] text-[#582D1D]',
    pptx: 'dark:text-[#FFE0C2] text-[#582D1D]',
    rar: 'dark:text-[#EDEEF0] text-[#2F265F]',
    rtf: 'dark:text-[#D6E1FF] text-[#1F2D5C]', // indigo-12
    tar: 'dark:text-[#EDEEF0] text-[#2F265F]',
    txt: 'text-n-slate-12',
    xls: 'text-n-teal-12',
    xlsx: 'text-n-teal-12',
    zip: 'dark:text-[#EDEEF0] text-[#2F265F]',
  };

  return colorMap[fileDetails.value.type] || 'text-n-slate-12';
});
</script>

<template>
  <div
    role="group"
    :aria-label="
      t('CONVERSATION.REPLYBOX.ATTACHMENT_LABEL', {
        type: fileTypeLabel,
        name: fullFileName,
      })
    "
    class="flex w-full max-w-md flex-col gap-2"
  >
    <div
      class="flex h-9 items-center gap-2 overflow-hidden rounded-lg border border-n-container bg-n-alpha-white px-2"
    >
      <FileIcon class="flex-shrink-0" :file-type="fileDetails.type" />
      <span
        class="min-w-0 flex-1 truncate text-sm"
        :title="fullFileName"
        :class="textColorClass"
      >
        {{ displayFileName }}
      </span>
      <a
        v-tooltip="
          t('CONVERSATION.REPLYBOX.DOWNLOAD_ATTACHMENT', {
            name: fullFileName,
          })
        "
        :aria-label="
          t('CONVERSATION.REPLYBOX.DOWNLOAD_ATTACHMENT', {
            name: fullFileName,
          })
        "
        class="grid size-9 flex-shrink-0 cursor-pointer place-content-center text-n-slate-11 transition-colors hover:text-n-slate-12"
        :href="attachment.dataUrl"
        rel="noreferrer noopener nofollow"
        target="_blank"
      >
        <Icon aria-hidden="true" icon="i-lucide-download" />
      </a>
    </div>

    <button
      v-if="canDescribeAttachment && !attachmentDescription"
      data-a11y-describe-attachment
      type="button"
      class="flex min-h-9 w-fit items-center gap-2 rounded-lg border border-n-container bg-n-alpha-1 px-3 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-wait disabled:opacity-60"
      :disabled="isDescribing"
      :aria-busy="isDescribing"
      @click="describeAttachment"
    >
      <Icon aria-hidden="true" icon="i-lucide-file-search" />
      {{
        isDescribing
          ? t('CONVERSATION.REPLYBOX.ATTACHMENT_DESCRIPTION_LOADING_SHORT')
          : t('CONVERSATION.REPLYBOX.DESCRIBE_ATTACHMENT')
      }}
    </button>

    <p
      v-if="attachmentDescription"
      role="status"
      class="mb-0 whitespace-pre-line rounded-lg bg-n-alpha-1 p-3 text-sm leading-relaxed text-n-slate-11"
    >
      <strong class="text-n-slate-12">
        {{ t('CONVERSATION.REPLYBOX.ATTACHMENT_DESCRIPTION_LABEL') }}
      </strong>
      {{ attachmentDescription }}
    </p>
  </div>
</template>
